import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Shield, ArrowLeft, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GoogleAuthButton from '../components/GoogleAuthButton';
import api from '../config/api';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [otp, setOtp] = useState('');
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formError, setFormError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const { sendOtp, verifyOtpAndRegister } = useAuth();
  const navigate = useNavigate();

  // Helper function to create pending profile after authentication
  const createPendingProfile = async () => {
    const pendingProfile = localStorage.getItem('pendingProfile');
    if (pendingProfile) {
      try {
        const profileData = JSON.parse(pendingProfile);
        const response = await api.post('/profiles', profileData);
        
        if (response.data.success) {
          localStorage.removeItem('pendingProfile');
          toast.success('Profile created successfully!');
          navigate('/profiles');
          return true;
        }
      } catch (error) {
        console.error('Failed to create pending profile:', error);
        // Continue even if profile creation fails
      }
    }
    return false;
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateField = (name, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    switch (name) {
      case 'firstName':
        if (!trimmedValue) return 'First name is required';
        if (trimmedValue.length < 2) return 'Please enter at least 2 characters';
        return '';
      case 'email':
        if (!trimmedValue) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) return 'Enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        if (!/[A-Za-z]/.test(value) || !/[0-9]/.test(value)) return 'Include both letters and numbers';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    ['firstName', 'email', 'password'].forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    });
    return validationErrors;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError('');
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...validationErrors }));
      setTouched((prev) => ({
        ...prev,
        firstName: true,
        email: true,
        password: true,
      }));
      toast.error('Please fix the highlighted fields');
      return;
    }

    setSendingOtp(true);
    setFormError('');

    const result = await sendOtp(
      formData.email,
      formData.password,
      formData.firstName,
      formData.lastName
    );
    setSendingOtp(false);

    if (result.success) {
      setShowOtpStep(true);
      setResendCooldown(60); // 60 second cooldown
      toast.success('OTP sent! Please check your email.');
    } else {
      const message = result.message || 'Failed to send OTP';
      setFormError(message);
      if (message.toLowerCase().includes('email')) {
        setErrors((prev) => ({ ...prev, email: message }));
        setTouched((prev) => ({ ...prev, email: true }));
      }
      toast.error(message);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setSendingOtp(true);
    setOtpError('');

    const result = await sendOtp(
      formData.email,
      formData.password,
      formData.firstName,
      formData.lastName
    );
    setSendingOtp(false);

    if (result.success) {
      setResendCooldown(60);
      setOtp('');
      toast.success('OTP resent! Please check your email.');
    } else {
      toast.error(result.message || 'Failed to resend OTP');
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setOtpError('');
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');

    if (otp.length !== 6) {
      setOtpError('Please enter a 6-digit OTP');
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);

    const result = await verifyOtpAndRegister(formData.email, otp);
    setVerifyingOtp(false);

    if (result.success) {
      toast.success('Registration successful!');
      
      // Check for pending profile data and create it
      const profileCreated = await createPendingProfile();
      if (!profileCreated) {
        navigate('/profiles/create');
      }
    } else {
      const message = result.message || 'OTP verification failed';
      setOtpError(message);
      toast.error(message);
    }
  };

  const handleBackToForm = () => {
    setShowOtpStep(false);
    setOtp('');
    setOtpError('');
    setResendCooldown(0);
  };

  const showError = (field) => touched[field] && errors[field];

  const isGoogleEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-16 flex-1">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-4xl font-black tracking-tight leading-none select-none">
                <span className="text-[#ff405c]">my</span>
                <span className="text-[#5c3fd8]">STORY</span>
                <span className="text-[#ff405c]">me</span>
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
            <p className="text-gray-600">Start creating magical stories for your child</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {showOtpStep ? (
              // OTP Verification Step
              <>
                <div className="mb-6 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
                  <p className="text-gray-600">
                    We've sent a 6-digit verification code to
                  </p>
                  <p className="text-gray-900 font-semibold mt-1">{formData.email}</p>
                </div>

                {otpError && (
                  <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-red-700">{otpError}</p>
                  </div>
                )}

                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Verification Code
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={otp}
                        onChange={handleOtpChange}
                        maxLength={6}
                        required
                        autoFocus
                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none text-center text-2xl font-mono tracking-widest ${
                          otpError ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                        }`}
                        placeholder="000000"
                      />
                    </div>
                    {otpError && (
                      <p className="text-xs text-red-600 mt-1">{otpError}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Enter the 6-digit code sent to your email
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={verifyingOtp || otp.length !== 6}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify & Complete Registration'}
                  </button>

                  <div className="text-center space-y-3">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={sendingOtp || resendCooldown > 0}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {sendingOtp ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : resendCooldown > 0 ? (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Resend OTP ({resendCooldown}s)
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          Resend OTP
                        </>
                      )}
                    </button>
                    <div>
                      <button
                        type="button"
                        onClick={handleBackToForm}
                        className="text-gray-600 hover:text-gray-700 font-medium text-sm inline-flex items-center gap-2"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back to registration form
                      </button>
                    </div>
                  </div>
                </form>
              </>
            ) : (
              // Registration Form Step
              <>
                {formError && (
                  <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-red-700">{formError}</p>
                  </div>
                )}
                {isGoogleEnabled && (
                  <div className="mb-8">
                    <GoogleAuthButton
                      textVariant="signup_with"
                      onSuccess={async () => {
                        const profileCreated = await createPendingProfile();
                        if (!profileCreated) {
                          navigate('/profiles/create');
                        }
                      }}
                    />
                    <div className="flex items-center gap-3 text-gray-400 text-sm mt-6">
                      <span className="h-px flex-1 bg-gray-200" />
                      <span>Or create with email</span>
                      <span className="h-px flex-1 bg-gray-200" />
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none ${
                        showError('firstName') ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                      }`}
                      placeholder="John"
                    />
                  </div>
                  {showError('firstName') && (
                    <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none ${
                      showError('email') ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                    }`}
                    placeholder="your@email.com"
                  />
                </div>
                {showError('email') && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    minLength={6}
                    className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none ${
                      showError('password') ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className={`text-xs mt-1 ${showError('password') ? 'text-red-600' : 'text-gray-500'}`}>
                  {showError('password') ? errors.password : 'Minimum 6 characters with letters and numbers'}
                </p>
              </div>

                  <button
                    type="submit"
                    disabled={sendingOtp}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingOtp ? 'Sending OTP...' : 'Send Verification Code'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    Already have an account?{' '}
                    <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">
                      Sign in here
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;

